import { ApiProperty } from '@nestjs/swagger';
import {
  IsOptional,
  IsString,
  IsDateString,
  IsEnum,
  IsNumber,
  Min,
  Max,
} from 'class-validator';

/**
 * DTO para filtros de merge PDF
 */
export class OSAgPdfMergeFilterDto {
  @ApiProperty({
    description: 'Tipo de archivo a incluir en el merge',
    example: 'invoice',
    required: false,
  })
  @IsOptional()
  @IsString()
  fileType?: string;

  @ApiProperty({
    description: 'Extensión de archivo específica',
    example: '.pdf',
    required: false,
  })
  @IsOptional()
  @IsString()
  extension?: string;

  @ApiProperty({
    description: 'Fecha desde para filtrar archivos',
    example: '2024-01-01',
    required: false,
  })
  @IsOptional()
  @IsDateString()
  dateFrom?: string;

  @ApiProperty({
    description: 'Fecha hasta para filtrar archivos',
    example: '2024-12-31',
    required: false,
  })
  @IsOptional()
  @IsDateString()
  dateTo?: string;

  @ApiProperty({
    description: 'Calidad de salida del PDF',
    enum: ['low', 'medium', 'high'],
    example: 'high',
    required: false,
  })
  @IsOptional()
  @IsEnum(['low', 'medium', 'high'])
  outputQuality?: 'low' | 'medium' | 'high';

  @ApiProperty({
    description: 'Nivel de compresión (0-9)',
    minimum: 0,
    maximum: 9,
    example: 6,
    required: false,
  })
  @IsOptional()
  @IsNumber()
  @Min(0)
  @Max(9)
  compressionLevel?: number;
}

/**
 * DTO para información de archivo en merge PDF
 */
export class OSAgPdfMergeFileInfoDto {
  @ApiProperty({
    description: 'ID del archivo',
    example: 1,
  })
  id!: number;

  @ApiProperty({
    description: 'Nombre del archivo',
    example: 'invoice_001.pdf',
  })
  fileName!: string;

  @ApiProperty({
    description: 'Tipo de archivo',
    example: 'invoice',
  })
  fileType!: string;

  @ApiProperty({
    description: 'Extensión del archivo',
    example: '.pdf',
  })
  extension!: string;

  @ApiProperty({
    description: 'Tamaño del archivo en bytes',
    example: 1048576,
  })
  fileSize!: number;

  @ApiProperty({
    description: 'Tamaño formateado del archivo',
    example: '1.0 MB',
  })
  fileSizeFormatted!: string;

  @ApiProperty({
    description: 'Fecha de creación del archivo',
    example: '2024-01-15T10:30:00Z',
  })
  creationDate!: Date;

  @ApiProperty({
    description: 'Estado del archivo en el merge',
    enum: ['pending', 'processing', 'completed', 'skipped', 'error'],
    example: 'completed',
  })
  status!: 'pending' | 'processing' | 'completed' | 'skipped' | 'error';

  @ApiProperty({
    description: 'Mensaje de estado o error del archivo',
    example: 'Archivo procesado exitosamente',
    required: false,
  })
  message?: string;

  @ApiProperty({
    description: 'Orden de aparición en el PDF final',
    example: 1,
    required: false,
  })
  order?: number;
}

/**
 * DTO para configuración del merge PDF
 */
export class OSAgPdfMergeConfigDto {
  @ApiProperty({
    description: 'Calidad de salida aplicada',
    example: 'high',
  })
  outputQuality!: 'low' | 'medium' | 'high';

  @ApiProperty({
    description: 'Nivel de compresión aplicado',
    example: 6,
  })
  compressionLevel!: number;

  @ApiProperty({
    description: 'Formato de salida del PDF',
    example: 'PDF/A',
  })
  outputFormat!: 'PDF' | 'PDF/A';

  @ApiProperty({
    description: 'Si se normalizó la versión del PDF',
    example: true,
  })
  normalizedPdfVersion!: boolean;

  @ApiProperty({
    description: 'Tamaño máximo por archivo permitido',
    example: '50 MB',
  })
  maxFileSize!: string;

  @ApiProperty({
    description: 'Tamaño total máximo permitido',
    example: '500 MB',
  })
  maxTotalSize!: string;

  @ApiProperty({
    description: 'Número máximo de archivos permitidos',
    example: 50,
  })
  maxFiles!: number;

  @ApiProperty({
    description: 'Timeout de procesamiento en segundos',
    example: 600,
  })
  timeout!: number;
}

/**
 * DTO para respuesta de merge PDF
 */
export class OSAgPdfMergeResponseDto {
  @ApiProperty({
    description: 'ID de la orden de servicio',
    example: '12345',
  })
  serviceOrderId!: string;

  @ApiProperty({
    description: 'URL de descarga del PDF consolidado',
    example: '/api/v1/os-agencias/files/12345/merge/download',
  })
  mergePdfUrl!: string;

  @ApiProperty({
    description: 'URL de descarga ZIP como alternativa',
    example: '/api/v1/os-agencias/files/12345/zip',
  })
  zipUrl!: string;

  @ApiProperty({
    description: 'Número total de archivos procesados',
    example: 15,
  })
  totalFiles!: number;

  @ApiProperty({
    description: 'Tamaño total de archivos procesados',
    example: '45.2 MB',
  })
  totalSize!: string;

  @ApiProperty({
    description: 'Tiempo estimado de procesamiento en segundos',
    example: 45,
  })
  estimatedProcessingTime!: number;

  @ApiProperty({
    description: 'Estado del procesamiento',
    enum: ['queued', 'processing', 'completed', 'failed'],
    example: 'queued',
  })
  status!: 'queued' | 'processing' | 'completed' | 'failed';

  @ApiProperty({
    description: 'Mensaje de estado o error',
    example: 'PDF consolidado generado exitosamente',
  })
  message!: string;

  @ApiProperty({
    description: 'Timestamp de creación de la solicitud',
    example: '2024-01-15T10:30:00Z',
  })
  createdAt!: Date;

  @ApiProperty({
    description: 'Timestamp de finalización del procesamiento',
    example: '2024-01-15T10:31:45Z',
    required: false,
  })
  completedAt?: Date;

  @ApiProperty({
    description: 'Información de archivos incluidos en el merge',
    type: [OSAgPdfMergeFileInfoDto],
    required: false,
  })
  files?: OSAgPdfMergeFileInfoDto[];

  @ApiProperty({
    description: 'Configuración aplicada al merge',
    type: OSAgPdfMergeConfigDto,
    required: false,
  })
  config?: OSAgPdfMergeConfigDto;
}

/**
 * DTO para solicitud de merge PDF
 */
export class OSAgPdfMergeRequestDto {
  @ApiProperty({
    description: 'Filtros opcionales para el merge',
    type: OSAgPdfMergeFilterDto,
    required: false,
  })
  @IsOptional()
  filters?: OSAgPdfMergeFilterDto;

  @ApiProperty({
    description: 'Si se debe incluir fallback a ZIP',
    example: true,
    required: false,
  })
  @IsOptional()
  includeFallbackZip?: boolean;

  @ApiProperty({
    description: 'Si se debe habilitar seguimiento de progreso',
    example: true,
    required: false,
  })
  @IsOptional()
  enableProgressTracking?: boolean;
}

/**
 * DTO para progreso del merge PDF
 */
export class OSAgPdfMergeProgressDto {
  @ApiProperty({
    description: 'ID de la orden de servicio',
    example: '12345',
  })
  serviceOrderId!: string;

  @ApiProperty({
    description: 'Estado actual del procesamiento',
    enum: ['queued', 'processing', 'completed', 'failed'],
    example: 'processing',
  })
  status!: 'queued' | 'processing' | 'completed' | 'failed';

  @ApiProperty({
    description: 'Porcentaje de completado (0-100)',
    minimum: 0,
    maximum: 100,
    example: 65,
  })
  @IsNumber()
  @Min(0)
  @Max(100)
  progress!: number;

  @ApiProperty({
    description: 'Archivo actual siendo procesado',
    example: 'invoice_001.pdf',
    required: false,
  })
  currentFile?: string;

  @ApiProperty({
    description: 'Archivos procesados hasta el momento',
    example: 10,
  })
  processedFiles!: number;

  @ApiProperty({
    description: 'Total de archivos a procesar',
    example: 15,
  })
  totalFiles!: number;

  @ApiProperty({
    description: 'Tiempo transcurrido en segundos',
    example: 30,
  })
  elapsedTime!: number;

  @ApiProperty({
    description: 'Tiempo estimado restante en segundos',
    example: 15,
    required: false,
  })
  estimatedTimeRemaining?: number;

  @ApiProperty({
    description: 'Mensaje de estado actual',
    example: 'Procesando archivo 11 de 15',
  })
  message!: string;

  @ApiProperty({
    description: 'Timestamp de la última actualización',
    example: '2024-01-15T10:30:30Z',
  })
  lastUpdated!: Date;
}

/**
 * DTO para errores del merge PDF
 */
export class OSAgPdfMergeErrorDto {
  @ApiProperty({
    description: 'Código de error',
    example: 'PDF_PASSWORD_PROTECTED',
  })
  errorCode!: string;

  @ApiProperty({
    description: 'Mensaje de error descriptivo',
    example: 'El archivo invoice_001.pdf está protegido con contraseña',
  })
  message!: string;

  @ApiProperty({
    description: 'Archivo que causó el error',
    example: 'invoice_001.pdf',
    required: false,
  })
  fileName?: string;

  @ApiProperty({
    description: 'Detalles adicionales del error',
    example: 'Se requieren credenciales para acceder al archivo',
    required: false,
  })
  suggestion?: string;

  @ApiProperty({
    description: 'Si se puede continuar con otros archivos',
    example: true,
  })
  canContinue!: boolean;

  @ApiProperty({
    description: 'Timestamp del error',
    example: '2024-01-15T10:30:00Z',
  })
  timestamp!: Date;
}

/**
 * DTO para información de error de archivo específico
 */
export class OSAgPdfMergeFileErrorDto {
  @ApiProperty({
    description: 'Nombre del archivo con error',
    example: 'invoice_001.pdf',
  })
  fileName!: string;

  @ApiProperty({
    description: 'Tipo de error',
    example: 'ENCRYPTED_PDF',
    enum: ['ENCRYPTED_PDF', 'CORRUPTED_PDF', 'READ_ERROR', 'UNKNOWN_ERROR'],
  })
  errorType!:
    | 'ENCRYPTED_PDF'
    | 'CORRUPTED_PDF'
    | 'READ_ERROR'
    | 'UNKNOWN_ERROR';

  @ApiProperty({
    description: 'Descripción detallada del error',
    example: 'El PDF está protegido con contraseña',
  })
  errorDescription!: string;

  @ApiProperty({
    description: 'Ruta del archivo',
    example: '/path/to/file.pdf',
  })
  filePath!: string;

  @ApiProperty({
    description: 'Tamaño del archivo en bytes',
    example: 57513,
  })
  fileSize!: number;
}

/**
 * DTO para respuesta de error detallada de merge PDF
 */
export class OSAgPdfMergeDetailedErrorDto {
  @ApiProperty({
    description: 'Indica si la operación fue exitosa',
    example: false,
  })
  success!: boolean;

  @ApiProperty({
    description: 'Mensaje de error general',
    example: 'Error al procesar archivos PDF',
  })
  message!: string;

  @ApiProperty({
    description: 'Código de error',
    example: 'PDF_PROCESSING_ERROR',
  })
  errorCode!: string;

  @ApiProperty({
    description: 'Lista de archivos con errores',
    type: [OSAgPdfMergeFileErrorDto],
  })
  fileErrors!: OSAgPdfMergeFileErrorDto[];

  @ApiProperty({
    description: 'ID de la orden de servicio',
    example: 12345,
  })
  serviceOrderId!: number;

  @ApiProperty({
    description: 'Timestamp del error',
    example: '2024-01-15T10:30:00Z',
  })
  timestamp!: string;
}
